import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLicense } from '../contexts/LicenseContext';
import PolicyGate from './PolicyGate';
import { Button } from './ui/button';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Home, Package, Users, ShoppingCart, FileText, Bell, LogOut,
  Truck, Shield, Building2, LayoutTemplate, Store, ClipboardList, Menu, X, DollarSign, Wallet, History, ShieldCheck, Database, Plus, Mail, CalendarDays, HandCoins, ContactRound, ClipboardCheck, BarChart3, Globe, KeyRound, AlertTriangle, Megaphone, ArrowRightLeft, FileSignature, CalendarClock
} from 'lucide-react';

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { license } = useLicense();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) return;
    let active = true;
    const fetchUnread = async () => {
      try {
        const res = await api.getNotifications();
        const list = Array.isArray(res.data) ? res.data : [];
        if (active) setUnreadCount(list.filter(n => !n.read).length);
      } catch (e) { /* noop */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 60000);
    return () => { active = false; clearInterval(t); };
  }, [user, location.pathname]);
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    loadCompanies();
    loadUserRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cierra el menú mobile al navegar
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const loadCompanies = async () => {
    try {
      const response = await api.getCompanies();
      const active = response.data || [];
      setCompanies(active);
      const isMaster = ['master', 'superadmin'].includes(user?.role_id);
      if (isMaster) {
        // Validar la selección guardada: si la empresa ya no está activa o no existe,
        // limpiar y caer a la empresa principal o la primera activa.
        const stored = localStorage.getItem('current_company_id');
        let resolved = active.find(c => c.company_id === stored)?.company_id;
        if (!resolved) resolved = active.find(c => c.company_id === user?.company_id)?.company_id;
        if (!resolved) resolved = active[0]?.company_id;
        if (resolved) localStorage.setItem('current_company_id', resolved);
        else localStorage.removeItem('current_company_id');
        setCurrentCompany(active.find(c => c.company_id === resolved) || null);
      } else {
        setCurrentCompany(active.find(c => c.company_id === user?.company_id) || active[0] || null);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const loadUserRole = async () => {
    try {
      const response = await api.getRoles();
      const role = response.data.find(r => r.role_id === user?.role_id);
      setUserRole(role);
    } catch (error) {
      console.error('Error loading user role:', error);
    }
  };

  const hasPermission = (requiredPermission) => {
    if (!userRole) return false;
    if (requiredPermission === 'dashboard') return true;
    if (userRole.permissions.includes('all')) return true;
    return userRole.permissions.includes(requiredPermission);
  };

  const handleChangeCompany = async (companyId) => {
    const company = companies.find(c => c.company_id === companyId);
    setCurrentCompany(company);
    // Para master/superadmin, NO persistir cambio en perfil (solo selección local)
    // Para otros roles, sí se persiste porque cambia su contexto de trabajo
    if (['master', 'superadmin'].includes(user?.role_id)) {
      // Solo guardar selección local sin tocar el perfil
      localStorage.setItem('current_company_id', companyId);
      window.location.reload();
      return;
    }
    try {
      await api.updateUser(user.user_id, { company_id: companyId });
      const updatedUser = { ...user, company_id: companyId };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      window.location.reload();
    } catch (error) {
      toast.error('SalesPRO: Error al cambiar de empresa');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Menú principal — filtrado por rol específico
  const allMenuItems = [
    // Cliente: SOLO Mi Portal + Notificaciones (modo comprador puro)
    { icon: Store, label: 'Mi Portal', path: '/client-portal', roles: ['cliente'], module: 'client_portal' },
    { icon: Bell, label: 'Notificaciones', path: '/notifications', roles: ['cliente'], module: 'notifications' },
    { icon: CalendarDays, label: 'Mis Vencimientos', path: '/calendar', roles: ['cliente'], module: 'calendar' },
    // Resto de roles
    { icon: Home, label: 'Dashboard', path: '/dashboard', roles: ['master','superadmin','admin','vendedor','despacho'], module: 'dashboard' },
    { icon: Building2, label: 'Empresas', path: '/companies', roles: ['master','superadmin'], module: 'companies' },
    { icon: Users, label: 'Usuarios', path: '/users', roles: ['master','superadmin','admin'], module: 'users' },
    { icon: Package, label: 'Inventario', path: '/inventory', roles: ['master','superadmin','admin'], module: 'inventory' },
    { icon: Users, label: 'Clientes', path: '/clients', roles: ['master','superadmin','admin','vendedor'], module: 'clients' },
    { icon: ShoppingCart, label: 'Ventas', path: '/sales', roles: ['master','superadmin','admin','vendedor'], module: 'sales' },
    { icon: Truck, label: 'Despacho', path: '/dispatch-queue', roles: ['master','superadmin','admin','despacho'], module: 'dispatch' },
    { icon: DollarSign, label: 'Finanzas', path: '/finance-pro', roles: ['master','superadmin','admin'], module: 'finance' },
    { icon: ShieldCheck, label: 'Auditoría', path: '/audit', roles: ['master','superadmin','admin'], module: 'audit' },
    { icon: Wallet, label: 'Nómina', path: '/payroll', roles: ['master','superadmin','admin'], module: 'payroll' },
    { icon: History, label: 'Histórico Precios', path: '/pricing-history', roles: ['master','superadmin'], module: 'pricing_history' },
    { icon: FileText, label: 'Reportes', path: '/reports', roles: ['master','superadmin','admin','vendedor'], module: 'reports' },
    { icon: Bell, label: 'Notificaciones', path: '/notifications', roles: ['master','superadmin','admin'], module: 'notifications' },
    { icon: Shield, label: 'Roles', path: '/roles', roles: ['master','superadmin'], module: 'roles' },
    { icon: Database, label: 'Despliegue', path: '/deployment', roles: ['master','superadmin'], module: 'deployment' },
    { icon: Globe, label: 'Vitrina / Tienda Web', path: '/web-presence', roles: ['master','superadmin'], module: 'store' },
    { icon: FileText, label: 'Diseño de Documentos', path: '/doc-config', roles: ['master','superadmin','admin'], module: 'store' },
    { icon: Mail, label: 'Marketing', path: '/marketing', roles: ['master','superadmin','admin'], module: 'marketing' },
    { icon: CalendarDays, label: 'Calendario', path: '/calendar', roles: ['master','superadmin','admin','vendedor'], module: 'calendar' },
    { icon: HandCoins, label: 'Cobranza', path: '/collections', roles: ['master','superadmin','admin','vendedor'], module: 'collections' },
    { icon: ContactRound, label: 'CRM Clientes', path: '/crm', roles: ['master','superadmin','admin','vendedor'], module: 'crm' },
    { icon: ClipboardCheck, label: 'Solicitudes y Pagos', path: '/credit-queue', roles: ['master','superadmin','admin'], module: 'credit_queue' },
    { icon: BarChart3, label: 'Reportes Sucursal', path: '/branch-reports', roles: ['master','superadmin','admin'], module: 'branch_reports' },
    { icon: KeyRound, label: 'Licencia y Funciones', path: '/license', roles: ['master'], module: 'license' },
    { icon: Building2, label: 'Vender Licencia', path: '/sell-license', roles: ['master'], module: 'license' },
    { icon: CalendarClock, label: 'Agenda Privada', path: '/master-agenda', roles: ['master'], module: 'license' },
    { icon: Megaphone, label: 'Promociones', path: '/promotions', roles: ['master','superadmin','admin'], module: 'promotions' },
    { icon: ArrowRightLeft, label: 'Traspasos', path: '/transfers', roles: ['master','superadmin','admin'], module: 'transfers' },
    { icon: FileSignature, label: 'Políticas y Firmas', path: '/policies', roles: ['master','superadmin'], module: 'policies' },
  ];

  // Filtra por rol y, si el usuario tiene módulos habilitados definidos (Master los controla),
  // por la lista de módulos permitidos. Master/superadmin siempre ven todo.
  // El Super Master controla qué funciones ve cada nivel (Licencia y Funciones); si la licencia
  // está suspendida solo quedan las funciones mínimas.
  const licMods = license?.modules;
  const menuItems = allMenuItems.filter(item => {
    if (!item.roles.includes(user?.role_id)) return false;
    if (user?.role_id === 'master') return true;
    if (Array.isArray(licMods)) return licMods.includes(item.module);
    return false;
  });

  const SidebarContent = ({ onNav }) => (
    <>
      <div className="p-6 border-b border-[#E2E8F0]">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-black text-[#022C22]">SalesPRO</h1>
            <p className="text-sm text-[#64748B] mt-1 truncate">{user?.full_name}</p>
            <p className="text-xs text-[#94A3B8] font-mono uppercase">{user?.role_id}</p>
          </div>
          <button onClick={() => setMobileOpen(false)} className="lg:hidden text-[#64748B] hover:text-[#022C22]">
            <X size={20} />
          </button>
        </div>
        {companies.length > 0 && (
          <div className="mt-3">
            <label className="text-xs text-[#94A3B8] mb-1 block">Empresa Actual</label>
            <select
              value={currentCompany?.company_id || ''}
              onChange={(e) => handleChangeCompany(e.target.value)}
              className="w-full px-3 py-2 bg-[#F8F9FA] rounded-md text-sm font-medium text-[#022C22] border border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="company-selector"
            >
              {companies.map((company) => (
                <option key={company.company_id} value={company.company_id}>{company.name}</option>
              ))}
            </select>
            {currentCompany?.logo_url && (
              <div className="mt-2 flex items-center justify-center p-2 bg-white rounded border border-[#E2E8F0]">
                <img
                  src={currentCompany.logo_url.startsWith('http') ? currentCompany.logo_url : api.fileUrl(currentCompany.logo_url)}
                  alt={currentCompany.name}
                  className="h-8 object-contain"
                  onError={(e) => { const p = e.target.parentElement; if (p) p.style.display = 'none'; }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {(() => {
          const SALESPRO_PATHS = ['/companies','/users','/roles','/deployment','/web-presence','/doc-config','/license','/sell-license','/master-agenda','/promotions','/policies','/pricing-history','/branch-reports','/credit-queue','/audit'];
          const ops = menuItems.filter((i) => !SALESPRO_PATHS.includes(i.path));
          const salespro = menuItems.filter((i) => SALESPRO_PATHS.includes(i.path));
          const renderItem = (item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                onClick={() => { navigate(item.path); onNav && onNav(); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                  isActive ? 'bg-[#F1F5F9] text-[#022C22]' : 'text-[#64748B] hover:bg-[#F8F9FA] hover:text-[#022C22]'
                }`}
              >
                <Icon size={20} />
                {item.label}
              </button>
            );
          };
          return (
            <>
              {ops.length > 0 && salespro.length > 0 && (
                <p className="px-4 pt-1 pb-2 text-[10px] font-black uppercase tracking-widest text-[#94A3B8]">Operación</p>
              )}
              {ops.map(renderItem)}
              {salespro.length > 0 && (
                <div className="mt-4 pt-3 border-t border-[#E2E8F0]" data-testid="salespro-workspace">
                  <p className="px-4 pb-2 text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-1.5">
                    <ShieldCheck size={12} /> Panel SalesPRO
                  </p>
                  {salespro.map(renderItem)}
                </div>
              )}
            </>
          );
        })()}
      </nav>

      <div className="p-4 border-t border-[#E2E8F0]">
        <Button
          data-testid="logout-button"
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white"
        >
          <LogOut size={18} />
          Cerrar Sesión
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-[#F8F9FA]">
      {/* Sidebar Desktop */}
      <aside className="hidden lg:flex lg:flex-col w-[280px] bg-white border-r border-[#E2E8F0]" data-testid="sidebar-desktop">
        <SidebarContent />
      </aside>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
          <aside className="fixed top-0 left-0 bottom-0 w-[280px] bg-white border-r border-[#E2E8F0] z-50 flex flex-col lg:hidden" data-testid="sidebar-mobile">
            <SidebarContent onNav={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0 w-full max-w-full">
        {/* Header Mobile + Desktop */}
        <header className="bg-white border-b border-[#E2E8F0] px-4 py-3 lg:px-6 lg:py-4" data-testid="app-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setMobileOpen(true)} className="lg:hidden text-[#022C22]" data-testid="mobile-menu-button">
                <Menu size={24} />
              </button>
              <div>
                <h1 className="lg:hidden font-heading text-lg font-black text-[#022C22]">SalesPRO</h1>
                <h2 className="hidden lg:block font-heading text-xl font-bold text-[#022C22]">
                  {menuItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {currentCompany && (
                <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-[#F8F9FA] rounded-md border border-[#E2E8F0]">
                  <Building2 size={14} className="text-primary" />
                  <span className="text-xs sm:text-sm font-medium truncate max-w-[150px]">{currentCompany.name}</span>
                </div>
              )}
              <button
                onClick={() => navigate('/notifications')}
                className="relative text-[#022C22] hover:text-primary p-2 rounded-md hover:bg-[#F0FBF7] transition-colors"
                data-testid="header-notifications-bell"
                title="Notificaciones"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full" data-testid="notif-unread-badge">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              <Button variant="ghost" size="icon" onClick={handleLogout} className="text-primary hover:text-primary hover:bg-primary/10" data-testid="logout-icon-header">
                <LogOut size={18} />
              </Button>
            </div>
          </div>
        </header>

        {license?.status === 'suspended' && (
          <div className="bg-red-600 text-white text-sm px-4 py-2 flex items-center gap-2" data-testid="license-suspended-banner">
            <AlertTriangle size={16} /> Licencia SalesPRO suspendida por falta de pago: solo están activas las funciones mínimas hasta que el Super Master confirme el pago.
          </div>
        )}
        {license?.show_renewal && license?.status !== 'suspended' && license?.days_left != null && license.days_left <= 7 && (
          <div className="bg-amber-500 text-white text-sm px-4 py-2 flex items-center gap-2" data-testid="license-renewal-banner">
            <AlertTriangle size={16} /> La licencia SalesPRO ({license.plan_name}) vence en {license.days_left} día(s): {license.renew_date}. Renueva tu plan para no perder funciones.
          </div>
        )}
        {/* Page Content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4 lg:p-6 pb-20 lg:pb-6 min-w-0 w-full max-w-full" data-testid="main-content">
          <PolicyGate><Outlet /></PolicyGate>
        </main>

        {/* Bottom nav móvil */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E2E8F0] flex justify-around items-center h-16 z-30" data-testid="bottom-nav-mobile">
          {menuItems.slice(0, 5).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center gap-1 px-2 py-2 ${isActive ? 'text-primary' : 'text-[#94A3B8]'}`}
              >
                <Icon size={22} />
                <span className="text-[10px] font-medium leading-tight">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Botón flotante global de venta (FAB) — iniciar venta desde cualquier sitio
            Se contrae a un pill compacto para no estorbar los clics. Expande con hover/focus.
            En móvil se posiciona arriba del bottom-nav (bottom-24) y en escritorio al borde (bottom-6). */}
        {user?.role_id !== 'cliente' && !['/companies', '/sales', '/sell-license'].includes(location.pathname) && (
          <button
            onClick={() => navigate('/sales?new=1')}
            data-testid="create-sale-fab"
            title="Nueva venta"
            aria-label="Nueva venta"
            className="group fixed right-3 bottom-24 lg:bottom-6 z-30 flex items-center justify-center gap-2 h-11 w-11 lg:h-12 lg:w-12 hover:w-auto hover:px-5 lg:hover:px-6 rounded-full bg-primary/90 hover:bg-primary text-primary-foreground shadow-md shadow-primary/30 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 font-heading font-bold overflow-hidden"
          >
            <Plus size={20} strokeWidth={2.5} className="shrink-0" />
            <span className="hidden group-hover:inline whitespace-nowrap text-sm">Nueva venta</span>
          </button>
        )}
      </div>
    </div>
  );
}
